import React, { useContext, useState, useMemo, useEffect } from "react";
import { Plus, Filter, ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useTickets from "../../shared/hooks/useTickets";
import ProductionValue from "../components/ProductionValue";
import { AuthContext } from "../../shared/context/auth-context";
import dateFormat, { masks } from "dateformat";
import { OverlayTrigger, Tooltip, Modal, Dropdown, Form } from "react-bootstrap";
import styles from "./Tickets.module.css"; // Assuming you have a CSS module for styles

export default function Tickets() {
	const navigate = useNavigate();
	const { tickets, isLoading } = useTickets();
	const { isSuperAdmin, isProducer } = useContext(AuthContext);
	const [showHelpModal, setShowHelpModal] = useState(false);
	const [showCompleted, setShowCompleted] = useState(() => {
		const saved = localStorage.getItem('tickets-show-completed');
		return saved ? JSON.parse(saved) : false;
	});
	const [sortBy, setSortBy] = useState(() => {
		const saved = localStorage.getItem('tickets-sort-by');
		return saved || "dueDate";
	});
	masks.displayDate = "ddd, mmmm d";

	// Save preferences to localStorage
	useEffect(() => {
		localStorage.setItem('tickets-show-completed', JSON.stringify(showCompleted));
	}, [showCompleted]);

	useEffect(() => {
		localStorage.setItem('tickets-sort-by', sortBy);
	}, [sortBy]);

	// Sort and filter tickets
	const processedTickets = useMemo(() => {
		if (!Array.isArray(tickets)) return [];

		// First, filter out completed tickets if showCompleted is false
		let filteredTickets = tickets.filter(ticket => {
			if (!showCompleted && ticket.status === "complete") {
				return false;
			}
			return true;
		});

		// Then sort based on the selected sort option
		if (sortBy === "dueDate") {
			// Sort by due date (earliest first), with null dates at the end
			filteredTickets.sort((a, b) => {
				const aDate = a.dueDate ? new Date(a.dueDate._seconds * 1000) : null;
				const bDate = b.dueDate ? new Date(b.dueDate._seconds * 1000) : null;
				
				if (!aDate && !bDate) return 0;
				if (!aDate) return 1; // null dates go to end
				if (!bDate) return -1; // null dates go to end
				
				return aDate - bDate; // earliest first
			});
		} else if (sortBy === "organization") {
			// Sort by organization, then by due date within each organization
			filteredTickets.sort((a, b) => {
				// First sort by organization
				const orgA = a.organization || "Unknown";
				const orgB = b.organization || "Unknown";
				const orgCompare = orgA.localeCompare(orgB);
				
				if (orgCompare !== 0) return orgCompare;
				
				// If organizations are the same, sort by due date
				const aDate = a.dueDate ? new Date(a.dueDate._seconds * 1000) : null;
				const bDate = b.dueDate ? new Date(b.dueDate._seconds * 1000) : null;
				
				if (!aDate && !bDate) return 0;
				if (!aDate) return 1; // null dates go to end
				if (!bDate) return -1; // null dates go to end
				
				return aDate - bDate; // earliest first
			});
		}

		return filteredTickets;
	}, [tickets, showCompleted, sortBy]);

	// Create grouped tickets for organization view
	const groupedTickets = useMemo(() => {
		if (sortBy !== "organization") return null;
		
		const groups = {};
		processedTickets.forEach(ticket => {
			const org = ticket.organization || "Unknown";
			if (!groups[org]) {
				groups[org] = [];
			}
			groups[org].push(ticket);
		});
		
		return groups;
	}, [processedTickets, sortBy]);

	// Function to render a single ticket row
	const renderTicketRow = (ticket) => {
		const isOverdue = ticket.dueDate && 
			new Date(ticket.dueDate._seconds * 1000) < new Date() && 
			ticket.status !== "complete";
		
		return (
			<tr
				key={ticket.id}
				className={`hover:bg-gray-50 cursor-pointer border-b ${isOverdue ? 'bg-red-50' : ''}`}
				onClick={() => navigate(`/tickets/${ticket.id}`)}
			>
			<td className="px-6 py-4 w-1/6">{ticket.title || "—"}</td>
			{(isSuperAdmin || isProducer) && (
				<>
					<td className="px-6 py-4">
						{ticket.createdBy || "Unknown"}
					</td>
					<td className="px-6 py-4">
						{ticket.organization || "Unknown"}
					</td>
				</>
			)}
			<td className="px-6 py-4 w-1/6">
				<span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
					{ticket.status &&
						ticket.status
							.split(" ")
							.map(
								(word) =>
									word.charAt(0).toUpperCase() + word.slice(1)
							)
							.join(" ")}
				</span>
			</td>
			<td className="px-6 py-4 w-1/6">
				<ProductionValue value={ticket.productionType} />
			</td>
			<td className="px-6 py-4 w-1/6">
				{ticket.dueDate ? (
					<div className="flex items-center space-x-2">
						<span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
							{dateFormat(
								new Date(ticket.dueDate._seconds * 1000),
								"displayDate"
							)}
						</span>
						{isOverdue && (
							<span className="text-red-600 text-xs font-bold">OVERDUE</span>
						)}
					</div>
				) : "—"}
			</td>
		</tr>
		);
	};

	// Function to render organization group header
	const renderOrgHeader = (orgName, ticketCount) => (
		<tr key={`org-${orgName}`} className="bg-gray-100">
			<td colSpan="6" className="px-6 py-2 text-sm font-semibold text-gray-700">
				{orgName} ({ticketCount} ticket{ticketCount !== 1 ? 's' : ''})
			</td>
		</tr>
	);

	return (
		<div className="space-y-4 flex-grow flex flex-col min-h-0 mt-8">
			<div className="flex justify-between items-center flex-shrink-0">
				<div className="flex items-center">
					<h2 className="text-lg font-semibold">Production Videos</h2>
					<OverlayTrigger
						placement="bottom" // Choose tooltip placement: top, right, bottom, left
						overlay={
							<Tooltip id="beta-tooltip">
								Click Here for a Tutorial!
							</Tooltip>
						}
					>
						<span className="beta-badge ms-3" onClick={() => setShowHelpModal(true)}>
							Need Help?
						</span>
					</OverlayTrigger>
				</div>

				<div className="flex items-center space-x-3">
					{/* Sort Controls */}
					<div className="flex items-center space-x-2">
						<ArrowUpDown className="w-4 h-4 text-gray-500" />
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm">
								Sort by: {sortBy === "dueDate" ? "Due Date" : "Organization"}
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item 
									active={sortBy === "dueDate"}
									onClick={() => setSortBy("dueDate")}
								>
									Due Date (Earliest First)
								</Dropdown.Item>
								<Dropdown.Item 
									active={sortBy === "organization"}
									onClick={() => setSortBy("organization")}
								>
									Organization (Then Due Date)
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>

					{/* Filter Controls */}
					<div className="flex items-center space-x-2">
						<Filter className="w-4 h-4 text-gray-500" />
						<Form.Check
							type="checkbox"
							label="Show Completed"
							checked={showCompleted}
							onChange={(e) => setShowCompleted(e.target.checked)}
							className="text-sm"
						/>
					</div>

					<button
						className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
						onClick={() => navigate("/tickets/new")}
					>
						<Plus className="w-4 h-4 mr-2" />
						Add New Video
					</button>
				</div>
			</div>

			{/* Summary/Status Bar */}
			<div className="flex items-center justify-between text-sm text-gray-600 px-2">
				<div>
					Showing {processedTickets.length} of {tickets.length} tickets
					{!showCompleted && ` (${tickets.filter(t => t.status === "complete").length} completed hidden)`}
					{(() => {
						const overdueCount = processedTickets.filter(t => 
							t.dueDate && 
							new Date(t.dueDate._seconds * 1000) < new Date() && 
							t.status !== "complete"
						).length;
						return overdueCount > 0 ? ` • ${overdueCount} overdue` : '';
					})()}
				</div>
				<div>
					Sorted by: {sortBy === "dueDate" ? "Due Date (Earliest First)" : "Organization → Due Date"}
				</div>
			</div>

			{/* Table container */}
			<div className="w-full overflow-hidden border rounded-lg flex-grow flex flex-col min-h-0 mt-4">
				<style>
					{`
			.scrollable-table {
			  max-height: 500px; /* change as needed */
			  overflow-y: auto; 
			  scrollbar-gutter: stable;
			}
			
			.scrollable-table::-webkit-scrollbar {
			  width: 8px;
			}
			.scrollable-table::-webkit-scrollbar-track {
			  background: transparent;
			}
			.scrollable-table::-webkit-scrollbar-thumb {
			  background-color: #E5E7EB;
			  border-radius: 20px;
			  border: 2px solid white;
			}
			.scrollable-table::-webkit-scrollbar-thumb:hover {
			  background-color: #D1D5DB;
			}
		  `}
				</style>

				{/* Table header */}
				<table className="w-full text-left border-b">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-6 py-3 text-sm font-semibold w-1/6">Title</th>
							{(isSuperAdmin || isProducer) && (
								<>
									<th className="px-6 py-3 text-sm font-semibold">Client</th>
									<th className="px-6 py-3 text-sm font-semibold">
										Organization
									</th>
								</>
							)}
							<th className="px-6 py-3 text-sm font-semibold w-1/6">Status</th>
							<th className="px-6 py-3 text-sm font-semibold w-1/6">
								Production Value
							</th>
							<th className="px-6 py-3 text-sm font-semibold w-1/6">
								Deadline
							</th>
						</tr>
					</thead>
				</table>

				{/* Table body scroll */}
				<div className="scrollable-table">
					<table className="w-full text-left">
						<tbody>
							{isLoading ? (
								/* Skeleton loading */
								Array(5)
									.fill(0)
									.map((_, index) => (
										<tr key={index} className="border-b">
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse" />
											</td>
											{(isSuperAdmin || isProducer) && (
												<>
													<td className="px-6 py-4">
														<div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse" />
													</td>
													<td className="px-6 py-4">
														<div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse" />
													</td>
												</>
											)}
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse" />
											</td>
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse" />
											</td>
											<td className="px-6 py-4">
												<div className="h-4 bg-gray-200 rounded w-1/6 animate-pulse" />
											</td>
										</tr>
									))
							) : !Array.isArray(processedTickets) || processedTickets.length === 0 ? (
								/* No tickets found */
								<tr>
									<td colSpan="5" className="px-6 py-4 text-center">
										{!showCompleted && tickets.length > 0 ? 
											"No active tickets found (completed tickets are hidden)" : 
											"No tickets found"
										}
									</td>
								</tr>
							) : sortBy === "organization" && groupedTickets ? (
								/* Render grouped by organization */
								Object.entries(groupedTickets).map(([orgName, orgTickets]) => [
									renderOrgHeader(orgName, orgTickets.length),
									...orgTickets.map(ticket => renderTicketRow(ticket))
								]).flat()
							) : (
								/* Map over processed tickets (due date sort) */
								processedTickets.map(ticket => renderTicketRow(ticket))
							)}
						</tbody>
					</table>
				</div>
			</div>
			<TutorialModal
				show={showHelpModal}
				onHide={() => {
					setShowHelpModal(false);
				}}
			/>
		</div>
	);
}

const TutorialModal = ({ show, onHide }) => {
	return (
		<Modal show={show} onHide={onHide} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title>🎉 New Features!</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p>
					Our new custom video feature gives you the power to create
					professional videos with ease. Here's what's new:
				</p>
				<ul>
					<li>
						🎥 Custom Video Creation: Upload your own videos, images, and logos.
					</li>
					<l1>🎬 One on one service with a professional video producer.</l1>
					<li>
						🔍 Video Preview: Iterate on multiple versions until you are
						satisfied.
					</li>
				</ul>
				<div className={styles.videoContainer}>
					<iframe
						src="https://drive.google.com/file/d/14I63NrOI2J55z7T-OIECcZ_WGPBehVnh/preview"
						allow="encrypted-media"
						allowFullScreen
						title="Tutorial Video"
						className={styles.videoFrame}
					></iframe>
				</div>
			</Modal.Body>
		</Modal>
	);
};
